import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ShortcutsContext = createContext(null);

export const targetSignature = (target) => {
  const ctx = target?.context || {};
  const keys = Object.keys(ctx).filter((k) => ctx[k] !== undefined && ctx[k] !== null).sort();
  return `${target?.tab || ''}|${keys.map((k) => `${k}=${JSON.stringify(ctx[k])}`).join('&')}`;
};

/**
 * Holds the user's pinned dashboard shortcuts plus a tiny registry of
 * "where am I right now".
 *
 * Nested screens (inventory sub-tab, ISO sub-tab, an open project) call
 * `useShortcutTarget(target, priority)`; the single pin button beside the
 * main tabs pins whichever registration has the highest priority, so it
 * always captures the deepest place the user is actually looking at.
 */
export const ShortcutsProvider = ({ children }) => {
  const [shortcuts, setShortcuts] = useState([]);
  const [targets, setTargets] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [barEnabled, setBarEnabledState] = useState(true);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/user-shortcuts`)
      .then((r) => { if (alive) setShortcuts(r.data || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoaded(true); });
    axios.get(`${API}/user-shortcut-prefs`)
      .then((r) => { if (alive) setBarEnabledState(r.data?.bar_enabled !== false); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const setBarEnabled = useCallback((on) => {
    setBarEnabledState(on);
    axios.put(`${API}/user-shortcut-prefs`, { bar_enabled: !!on }).catch(() => {});
  }, []);

  const registerTarget = useCallback((priority, target) => {
    setTargets((prev) => {
      if (!target) {
        if (!(priority in prev)) return prev;
        const next = { ...prev };
        delete next[priority];
        return next;
      }
      const old = prev[priority];
      if (old && targetSignature(old) === targetSignature(target)
        && old.label === target.label && old.subtitle === target.subtitle) return prev;
      return { ...prev, [priority]: { ...target, _priority: priority } };
    });
  }, []);

  const currentTarget = useMemo(() => {
    const keys = Object.keys(targets).map(Number).sort((a, b) => b - a);
    return keys.length ? targets[keys[0]] : null;
  }, [targets]);

  const findPinned = useCallback(
    (target) => {
      if (!target) return null;
      const sig = targetSignature(target);
      return shortcuts.find((s) => targetSignature(s) === sig) || null;
    },
    [shortcuts],
  );

  const addShortcut = useCallback(async (target) => {
    const r = await axios.post(`${API}/user-shortcuts`, {
      label: (target.label || '').slice(0, 60),
      tab: target.tab,
      context: target.context || null,
      icon: target.icon || 'star',
      color: target.color || 'slate',
      perms: target.perms || [],
      subtitle: (target.subtitle || '').slice(0, 80),
    });
    setShortcuts(r.data || []);
  }, []);

  const removeShortcut = useCallback(async (id) => {
    const r = await axios.delete(`${API}/user-shortcuts/${id}`);
    setShortcuts(r.data || []);
  }, []);

  const renameShortcut = useCallback(async (id, label) => {
    const r = await axios.patch(`${API}/user-shortcuts/${id}`, { label });
    setShortcuts(r.data || []);
  }, []);

  const moveShortcut = useCallback(async (id, delta) => {
    const idx = shortcuts.findIndex((s) => s.id === id);
    const next = idx + delta;
    if (idx < 0 || next < 0 || next >= shortcuts.length) return;
    const reordered = [...shortcuts];
    [reordered[idx], reordered[next]] = [reordered[next], reordered[idx]];
    setShortcuts(reordered);
    try {
      const r = await axios.put(`${API}/user-shortcuts/order`, { ids: reordered.map((s) => s.id) });
      setShortcuts(r.data || reordered);
    } catch {
      setShortcuts(shortcuts);
    }
  }, [shortcuts]);

  const value = useMemo(() => ({
    shortcuts, loaded, currentTarget, registerTarget, barEnabled, setBarEnabled,
    findPinned, addShortcut, removeShortcut, renameShortcut, moveShortcut,
  }), [shortcuts, loaded, currentTarget, registerTarget, barEnabled, setBarEnabled, findPinned,
    addShortcut, removeShortcut, renameShortcut, moveShortcut]);

  return <ShortcutsContext.Provider value={value}>{children}</ShortcutsContext.Provider>;
};

export const useShortcuts = () => useContext(ShortcutsContext) || {
  shortcuts: [], loaded: false, currentTarget: null, registerTarget: () => {},
  barEnabled: true, setBarEnabled: () => {},
  findPinned: () => null, addShortcut: async () => {}, removeShortcut: async () => {},
  renameShortcut: async () => {}, moveShortcut: async () => {},
};

/** Declare the pinnable location of the screen that is currently on top. */
export const useShortcutTarget = (target, priority = 1) => {
  const { registerTarget } = useShortcuts();
  const sig = target ? `${targetSignature(target)}|${target.label}|${target.subtitle || ''}` : '';
  const ref = useRef(target);
  ref.current = target;
  useEffect(() => {
    registerTarget(priority, ref.current);
    return () => registerTarget(priority, null);
  }, [registerTarget, priority, sig]);
};

export default ShortcutsContext;
